import { useEffect, useRef } from "react";

import { Column, FormField, NumberInput, Text } from "@hightouch/htui";

import { usePredictiveTraitArtifactsQuery } from "src/graphql";
import { getCatalogTopK } from "src/components/traits/predictive-utils";
import {
  type PropertyCondition,
  isRelatedColumn,
  isTraitColumn,
} from "src/types/visual";

import { normalizeCatalogRankCondition } from "./catalog-recommendation-rank";
import type { FilterProps } from "./condition";
import { Filter } from "./filter";

const INTEGER_FORMAT: Intl.NumberFormatOptions = { maximumFractionDigits: 0 };

type CatalogRecommendationRankFilterProps = Pick<
  FilterProps<PropertyCondition>,
  "onUpdate" | "condition" | "isDisabled"
> & {
  valueError: string | null | undefined;
};

export const CatalogRecommendationRankFilter = ({
  onUpdate,
  condition,
  isDisabled,
  valueError,
}: CatalogRecommendationRankFilterProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const traitId =
    isRelatedColumn(condition.property) &&
    isTraitColumn(condition.property.column)
      ? condition.property.column.traitDefinitionId
      : "";

  const { data } = usePredictiveTraitArtifactsQuery(
    {
      traitId,
      runsWhere: {
        _and: [
          { job_type: { _eq: "catalog-inference" } },
          { status: { _eq: "success" } },
        ],
      },
    },
    { enabled: Boolean(traitId) },
  );
  const topK = getCatalogTopK(data?.trait_definitions_by_pk?.predictor?.config);

  const normalized = normalizeCatalogRankCondition(condition);
  const needsNormalizing =
    condition.operator !== normalized.operator ||
    condition.value !== normalized.value;

  useEffect(() => {
    if (needsNormalizing) {
      onUpdate((condition) => {
        condition.operator = normalized.operator;
        condition.value = normalized.value;
      });
    }
  }, [needsNormalizing, normalized.operator, normalized.value, onUpdate]);

  const content = (
    <Column p={2} width="xs">
      <FormField
        label="Recommendation rank"
        description={`Matches profiles with a matching item in their top N recommendations. Each profile has up to ${topK}.`}
      >
        <NumberInput
          ref={inputRef}
          formatOptions={INTEGER_FORMAT}
          isDisabled={isDisabled}
          isInvalid={Boolean(valueError)}
          max={topK}
          min={1}
          step={1}
          value={normalized.value}
          width="100%"
          onChange={(value) => {
            if (value != null) {
              onUpdate((condition) => {
                condition.value = value;
              });
            }
          }}
        />
      </FormField>
    </Column>
  );

  return (
    <Filter
      content={content}
      error={valueError}
      initialFocusRef={inputRef}
      isDisabled={isDisabled}
    >
      <Text>
        <Text color="text.secondary">is in top</Text>{" "}
        <Text variant="numeric">{normalized.value}</Text>
      </Text>
    </Filter>
  );
};
